import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { IUiSchema } from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { IContactListDataSourceItem } from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import {
  contactSelectInlineV2ValueSelector,
  getValueOrExtraValue,
} from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import {
  ERegistrationContractDestination,
  ERegistrationContractLanguage,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  EAutoRegistrationKind,
  getRegistrationPartyAccounts,
  isPropertyTaxRelevantForDestination,
} from "@rentiohq/shared-frontend/dist/utils/autoRegistration.utils";
import {
  getLocalizedText,
  getOptionalLocalizedEnumText,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  getAutoRegistrationCompanyFieldsSchema,
  getIncompleteRegistrationParties,
  getRegistrationContactFieldsSchema,
} from "@rentiohq/shared-frontend/dist/utils/registration.utils";
import { JSONSchema6 } from "json-schema";
import sortBy from "lodash/sortBy";
import { FormValidation } from "react-native-jsonschema-form-test";
import {
  getAutoRegistrationContext,
  getAutoRegistrationOptIn,
  getDefaultDestinationForForm,
  getRegistrationPartyContacts,
  getOptInAnswerKey,
} from "./schema.createDocumentPackage.7.utils";
import { EField, IExtraData } from "./schema.createDocumentPackage.types";
import {
  getNavigationTitle,
  getRelatedContract,
} from "./schema.createDocumentPackage.utils";

// The option getters below are functions, not constants: getLocalizedText must
// run after setLocale (index.tsx), which happens after this module loads.

// Same options, labels and sorting the manual registration wizard offers.
const getDestinationOptions = () =>
  sortBy(
    Object.entries(ERegistrationContractDestination).map(([key, value]) => ({
      id: value,
      value: getLocalizedText(
        `registration.contract_destination.${key.toLowerCase()}`,
      ),
    })),
    option => option.value,
  );

const getLanguageOptions = () =>
  Object.values(ERegistrationContractLanguage).map(language => ({
    id: language,
    value: getLocalizedText(
      `registration.preferred_language.${language.toLowerCase()}`,
    ),
  }));

const getAutoRegistrationOptInOptions = () =>
  [true, false].map(id => ({
    id,
    value: getLocalizedText(`system.${id ? "yes" : "no"}`),
  }));

const getTaxPaidByTenantOptions = () =>
  ["yes", "no"].map(id => ({
    id,
    value: getLocalizedText(`system.${id}`),
  }));

/**
 * The effective answer, which is not the same as the stored bit.
 *
 * The wizard keeps each step's last submitted values in `partialFormData`, and
 * the summary and the submit both read that merged map - so an opt-in given for
 * an eligible lease survives a switch to a lease that cannot be registered
 * (edited from the summary) until step 7 is submitted again. Reading the raw
 * bit anywhere would tell the broker their lease will be registered while the
 * create path drops it. Everything here reads the effective answer instead.
 */
// JSONSchema6 does not declare the properties the form framework reads off a
// field: `showIf` / `requiredIf`, which Form.getSchemaWithConditionalFields
// calls with (formData, schema), and `options`, which the DropdownSelect and
// SegmentedControl renderers read. Declaring them here rather than suppressing
// per line keeps the compiler on a misspelled key and on a predicate whose
// signature drifts - a silently ignored `requiredIf` would let the step submit
// without its required fields. Same shape as ISchemaFieldWithOptions in
// schema.editPropertyInfo.1.ts.
type TAutoRegistrationField = JSONSchema6 & {
  showIf?: (formData: any, schema: JSONSchema6) => boolean;
  requiredIf?: (formData: any, schema: JSONSchema6) => boolean;
  // The opt-in's ids are booleans: web-shared's SegmentedControl accepts them
  // (its TId is string | number | boolean), so the answer is stored as the same
  // boolean the switch it replaced wrote, and no reader has to change.
  options?: { id: string | boolean; value: string }[];
};

const isAutoRegistrationOn = (extraData: IExtraData) => (formData: any) =>
  !!getAutoRegistrationOptIn({ formData, extraData }) &&
  !getAutoRegistrationContext({ formData, extraData }).disabledReason;

/**
 * Whether the step still has to collect what a new registration needs.
 *
 * Only the lease contract creates one. An inspection report is attached to the
 * lease's existing registration, which already carries the destination, the
 * language and the parties MyRent accepted - so asking for them again would
 * collect answers nothing sends.
 */
const isCreatingRegistration = (extraData: IExtraData) => (formData: any) =>
  isAutoRegistrationOn(extraData)(formData) &&
  getAutoRegistrationContext({ formData, extraData }).kind ===
    EAutoRegistrationKind.Contract;

export const getSchema =
  (_: { [key: string]: any }, extraData: IExtraData) =>
  (defaultValues: { [key: string]: any }): JSONSchema6 => {
    const { kind, disabledReason } = getAutoRegistrationContext({
      formData: defaultValues,
      extraData,
    });

    const isCreating = isCreatingRegistration(extraData);

    const properties: { [key: string]: TAutoRegistrationField } = {
      [EField.Header + "7"]: {
        default: undefined,
      },
      [EField.AutoRegisterMyRent]: {
        // AC1/AC4: asked from scratch every time, never remembered - see
        // getAutoRegistrationOptIn for where it starts. A lease that cannot be
        // registered shows no, whatever was stored for it.
        default:
          kind && disabledReason
            ? false
            : getAutoRegistrationOptIn({ formData: defaultValues, extraData }),
        options: getAutoRegistrationOptInOptions(),
      },
      [EField.AutoRegisterMyRentAnsweredFor]: {
        // Always the document and lease on screen, so whenever the step stores
        // the opt-in, it stores this too.
        type: "string",
        default: getOptInAnswerKey(defaultValues),
      },
      [EField.AutoRegistrationDisabledInfo]: {
        default: undefined,
        showIf: () => !!disabledReason,
      },
      [EField.RegistrationDestination]: {
        // AC9: prefilled from the lease and property type. Every step's
        // schema is also evaluated once against an empty form at form
        // creation (form.reducer createForm), which seeds partialFormData, so
        // a plain `key in defaultValues` check would pin this to the value
        // derived before any contract was picked. Hence the key check is off
        // and the derivation yields undefined while the lease is unknown.
        default: getValueOrExtraValue(
          EField.RegistrationDestination,
          defaultValues,
          getDefaultDestinationForForm({
            formData: defaultValues,
            extraData,
          }),
          false,
        ),
        options: getDestinationOptions(),
        requiredIf: isCreating,
        showIf: isCreating,
      },
      [EField.RegistrationLanguage]: {
        default: getValueOrExtraValue(
          EField.RegistrationLanguage,
          defaultValues,
          ERegistrationContractLanguage.Dutch,
        ),
        options: getLanguageOptions(),
        requiredIf: isCreating,
        showIf: isCreating,
      },
      [EField.RegistrationTaxPaidByTenant]: {
        default: getValueOrExtraValue(
          EField.RegistrationTaxPaidByTenant,
          defaultValues,
          "no",
        ),
        options: getTaxPaidByTenantOptions(),
        // AC11: MyRent only accepts the answer for a non-housing lease.
        showIf: (formData: any) =>
          isCreating(formData) &&
          isPropertyTaxRelevantForDestination(
            formData[EField.RegistrationDestination],
          ),
      },
      [EField.RegistrationMembersInfo]: {
        default: undefined,
        showIf: isCreating,
      },
      [EField.RegistrationMembers]: {
        // Always derived, never picked: the parties follow from the contract
        // members and the sign-as-broker answer, so a value carried over from
        // an earlier pass through this step would go stale.
        default: getAutoRegistrationContext({
          formData: defaultValues,
          extraData,
        }).partyAccountIds,
        requiredIf: isCreating,
        showIf: isCreating,
      },
    };

    return {
      // AC26: `required` is not filtered by `showIf` (Form.getSchemaWithConditionalFields),
      // so a plain signing package answering no would be blocked by
      // fields it never shows. `requiredIf` is evaluated against the live form
      // data and is the mechanism the manual wizard uses for the same reason.
      required: [],
      properties,
    };
  };

export const uiSchema = (extraData: IExtraData): IUiSchema<EField> => ({
  "rentio:title": getLocalizedText(
    "document_package.form.auto_registration.step_heading",
  ),
  "rentio:navigationTitle": getNavigationTitle(),
  [EField.Header + "7"]: {
    "ui:field": ERentioFormField.SectionHeader,
    title: getLocalizedText("document_package.form.auto_registration.heading"),
  },
  [EField.AutoRegisterMyRent]: {
    // A yes/no control rather than a switch, like the property-tax question
    // below: a switch sets the question in the header style and pushes the
    // toggle to the far edge of the step.
    "ui:field": ERentioFormField.SegmentedControl,
    // The question is the same, but it is asked about the document being
    // signed, so the inspection report names itself rather than "this
    // contract". The kind follows the lease and the document, both of which the
    // wizard can still change, so this is read per render.
    getTitle: formData =>
      getLocalizedText(
        getAutoRegistrationContext({ formData, extraData }).kind ===
          EAutoRegistrationKind.Pir
          ? "document_package.form.auto_registration.toggle.label.location_description"
          : "document_package.form.auto_registration.toggle.label",
      ),
    isDisabled: (formData: any) =>
      !!getAutoRegistrationContext({ formData, extraData }).disabledReason,
    formatSummary: (_: boolean, formData: any) =>
      getLocalizedText(
        `system.${isAutoRegistrationOn(extraData)(formData) ? "yes" : "no"}`,
      ),
  },
  [EField.AutoRegisterMyRentAnsweredFor]: {
    "ui:widget": "hidden",
  },
  [EField.AutoRegistrationDisabledInfo]: {
    "ui:field": ERentioFormField.HelpInfo,
    getSubtitle: (_: IPartialRootState, formData: any) => {
      const { disabledReason } = getAutoRegistrationContext({
        formData,
        extraData,
      });

      if (!disabledReason) {
        return;
      }

      return getLocalizedText(
        `document_package.form.auto_registration.disabled.${disabledReason}`,
      );
    },
  },
  [EField.RegistrationDestination]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText(
      "registration.create.form.field.contract_destination",
    ),
    formatSummary: (value?: ERegistrationContractDestination) =>
      getOptionalLocalizedEnumText({
        prefix: "registration.contract_destination",
        value,
      }),
  },
  [EField.RegistrationLanguage]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText(
      "registration.create.form.field.preferred_language",
    ),
    formatSummary: (value?: ERegistrationContractLanguage) =>
      getOptionalLocalizedEnumText({
        prefix: "registration.preferred_language",
        value,
      }),
  },
  [EField.RegistrationTaxPaidByTenant]: {
    "ui:field": ERentioFormField.SegmentedControl,
    extraInfoUrl:
      "registration.create.form.field.rent_contract_property_tax_paid_by_tenant.url",
    title: getLocalizedText(
      "registration.create.form.field.rent_contract_property_tax_paid_by_tenant",
    ),
    // Hidden for a housing lease, so the summary is handed no value at all.
    formatSummary: (value?: string) =>
      getOptionalLocalizedEnumText({ prefix: "system", value }),
  },
  [EField.RegistrationMembersInfo]: {
    "ui:field": ERentioFormField.HelpInfo,
    subtitle: getLocalizedText(
      "document_package.form.auto_registration.members.info",
    ),
  },
  [EField.RegistrationMembers]: {
    "ui:field": ERentioFormField.ContactSelectInlineV2,
    title: getLocalizedText(
      "document_package.form.auto_registration.members.heading",
    ),
    isMultiSelect: true,
    // Deliberately not an option list: the parties are fixed, and a disabled
    // option list also disables the inline "complete missing fields" link that
    // is the whole point of showing them. readOnly keeps the list fixed while
    // leaving that link and the edit action usable.
    asOptionList: false,
    readOnly: true,
    getRequiredContactFieldsSchema: getRegistrationContactFieldsSchema,
    getRequiredCompanyFieldsSchema: getAutoRegistrationCompanyFieldsSchema,
    selectContactFromListCtaTitle: "",
    valueSelector: (
      state: IPartialRootState,
      selectedAccountIds: number[] | string = [],
      formData: any,
    ) =>
      contactSelectInlineV2ValueSelector(
        state,
        selectedAccountIds,
        getAutoRegistrationAccounts({ formData, extraData }),
      ),
    customGetSubtitleSelector: (
      _: IPartialRootState,
      formData: any,
      item: IContactListDataSourceItem,
    ) => getPartyRole({ formData, extraData, item }),
    customExtraAccountsSelector: (_: IPartialRootState, formData: any) =>
      getAutoRegistrationAccounts({ formData, extraData }),
    customIsFetchingSelector: () => false,
    customGetContactsActions: [],
    getDisallowedAccountIds: (_: any) => [],
  },
});

const getAutoRegistrationAccounts = (params: {
  formData: any;
  extraData: IExtraData;
}) => {
  const { formData, extraData } = params;
  const { partyAccountIds } = getAutoRegistrationContext(params);

  return getRegistrationPartyAccounts({
    contractMembers: getRelatedContract(extraData, formData)?.members,
    legalContact: extraData.legalContact,
    contactMeMaster: extraData.contactMeMaster,
    partyAccountIds,
  });
};

const getPartyRole = (params: {
  formData: any;
  extraData: IExtraData;
  item: IContactListDataSourceItem;
}) => {
  const { formData, extraData, item } = params;
  const { accountId, contact } = item;

  const { members } = getAutoRegistrationContext({ formData, extraData });

  const member = members.find(
    registrationMember =>
      registrationMember.accountId === accountId ||
      contact?.accountIds.includes(registrationMember.accountId),
  );

  if (member) {
    return getLocalizedText(`role.${member.role.toLowerCase()}`);
  }

  // The broker is checked through the legal contact, which is not itself a
  // registration member, so it never matches above.
  if (contact && contact.id === extraData.legalContact?.id) {
    return getLocalizedText("role.broker");
  }

  return;
};

export const getValidate =
  (extraData: IExtraData) =>
  (
    stepFormData: { [key: string]: unknown },
    errors: FormValidation,
    mergedPartialFormData?: { [key: string]: unknown },
  ) => {
    // On the step's Next, Form.validate passes only this step's answers, and
    // the lease they depend on was picked on an earlier step - so the stored
    // answers of every step are read too, with this step's own winning. The
    // summary passes every answer as the form data and nothing else.
    const formData = { ...mergedPartialFormData, ...stepFormData };

    // The summary shows the question with a starting answer it was never put
    // for when the step holds no answer for the document and lease being
    // signed: the step first appeared through a change made from the summary,
    // or the document or the lease changed after it was answered; a property
    // edit alone keeps both. "Save and send" then waits until the step has
    // stored its answers for them: on Next or Update, or already on opening
    // when that reveals a field (the property-tax question of a non-housing
    // lease). A disabled question is not held: there is nothing to answer, and
    // the submit sends no registration for it. On the step's own Next this
    // never fires: its live answers carry the document and lease on screen.
    const { kind, disabledReason } = getAutoRegistrationContext({
      formData,
      extraData,
    });

    if (
      kind &&
      !disabledReason &&
      formData[EField.AutoRegisterMyRentAnsweredFor] !==
        getOptInAnswerKey(formData)
    ) {
      errors[EField.AutoRegisterMyRent]?.addError(
        getLocalizedText(
          "document_package.form.auto_registration.toggle.error.unanswered",
        ),
      );
    }

    // The effective answer, not the stored bit: a disabled opt-in collects
    // nothing, so there is nothing to gate on - and erroring there would strand
    // the broker, since FormSummary runs every step's validate and a disabled
    // control cannot be set to no to clear the error. An inspection report
    // collects no parties either: MyRent already accepted the ones on the
    // registration it is attached to.
    if (!isCreatingRegistration(extraData)(formData)) {
      return errors;
    }

    const { partyAccountIds } = getAutoRegistrationContext({
      formData,
      extraData,
    });

    // AC12: ContactSelect warns on an incomplete party but does not block, so
    // the step gates on it here, on Next and again in the summary. Without this
    // a registration is created that MyRent rejects at signing completion.
    const incomplete = getIncompleteRegistrationParties(
      getRegistrationPartyContacts(partyAccountIds),
    );

    if (incomplete.length > 0) {
      errors[EField.RegistrationMembers]?.addError(
        getLocalizedText("form.complete_missing_fields.message"),
      );
    }

    return errors;
  };
